import { Button, Card, CardContent, StandaloneLink } from '@/components/wm';
import { apiRequest } from '@/utils/request';
import { ChevronDown, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { addSpacersToFeatures, CardFooter, ConditionsList, ExtraSection, FeatureList, getAllowedTransitions } from '../index';

export default function Plans ({ organization, currentPlan, plans }) {
    const [showExtraPlans, setShowExtraPlans] = React.useState(false);
    const allowedTransitions = getAllowedTransitions(currentPlan.slug);

    // Get essentials plan from plans array
    const essentialsPlan = plans.find(plan => plan.slug === 'essentials');
    const renderExtraPlans = WiseMetering.Permissions.hasRolePermissions('plans', 'update') && currentPlan.slug === 'free' && organization.type === 'site';

    const handleChangePlan = async planSlug => {
        const selectedPlan = WiseMetering.plans.find(t => t.get('slug') === planSlug);

        try {
            await apiRequest(`/organizations/${organization.id}/actions/change_plan`, 'PUT', { plan_id: selectedPlan.id });
            WiseMetering.layout.showTipper('success', i18next.t('plans.changed_successfully', { plan: selectedPlan.slug }));
            setTimeout(() => window.location.reload(), 1000);
        } catch (err) {
            WiseMetering.layout.showTipper('error', i18next.t('plans.contact_support'));
        }
    };

    const gridColsClass = currentPlan.slug === 'free' || currentPlan.slug === 'essentials' ? 'lg:tw-grid-cols-4' : 'lg:tw-grid-cols-3';

    return (
        <>
            <h2 className="tw-text-2xl tw-font-bold tw-mb-4">{i18next.t('plans.wise_metering_subscription_plans')}</h2>
            <div className={`tw-grid tw-grid-cols-1 md:tw-grid-cols-2 ${gridColsClass} tw-gap-4`}>
                {plans.map(plan => {
                    // Check if this plan should be rendered in the main grid
                    if (plan.slug === 'free' && currentPlan.slug !== 'free') {
                        return null;
                    }

                    if (plan.slug === 'essentials' && currentPlan.slug !== 'essentials') {
                        return null;
                    }

                    const isCurrent = currentPlan.slug === plan.slug;
                    const cardClass = isCurrent ? 'card plan selected tw-h-full tw-p-4 tw-flex tw-flex-col tw-relative' : 'card plan tw-h-full tw-p-4 tw-flex tw-flex-col';

                    return (
                        <Card
                            className={cardClass}
                            key={plan.key}
                        >
                            {isCurrent && <div className="ribbon"></div>}
                            <div className="tw-flex tw-flex-col tw-items-center tw-mb-4">
                                <div className="tw-text-center tw-font-bold tw-text-lg tw-mb-0.5 tw-text-primary">{plan.key}</div>
                                <h4 className="tw-font-bold tw-text-2xl !tw-mb-0">€{plan.price}/mo</h4>
                                <span className="tw-text-xs tw-text-muted-foreground">{i18next.t('plans.starting_at')}</span>
                            </div>
                            <CardContent className="tw-flex-1 tw-flex tw-flex-col">
                                <div className="tw-flex tw-flex-col">
                                    <div>
                                        <p className="tw-font-bold tw-mb-2">{i18next.t('plans.features_label')}:</p>
                                        <FeatureList className="tw-text-primary" features={addSpacersToFeatures(plan.features, plans)}/>
                                    </div>
                                    <div className="tw-border-t tw-border-solid tw-border-primary tw-my-3"></div>
                                    <div>
                                        <ConditionsList className="tw-text-primary" conditions={plan.conditions}/>
                                    </div>
                                </div>
                                <ExtraSection className="tw-text-primary" extra={plan.extra}/>
                                {
                                    WiseMetering.Permissions.hasRolePermissions('plans', 'update') && (
                                        <CardFooter>
                                            {isCurrent && <div className="tw-text-secondary tw-text-center tw-font-bold">{i18next.t('plans.current_plan')}</div>}
                                            {allowedTransitions[plan.slug] && !isCurrent && (
                                                <Button className="tw-w-full" onClick={() => handleChangePlan(plan.slug)} variant="secondary">
                                                    {`${i18next.t('plans.switch_to')} ${plan.key}`}
                                                </Button>
                                            )}
                                            {!isCurrent && !allowedTransitions[plan.slug] && (
                                                <span className="tw-invisible">Button placeholder</span>
                                            )}
                                        </CardFooter>
                                    )
                                }
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {essentialsPlan && renderExtraPlans && (
                <div className="tw-mt-4 tw-border tw-border-solid tw-border-border tw-rounded-lg tw-overflow-hidden">
                    <button
                        className="tw-w-full tw-p-4 tw-flex tw-items-center tw-justify-between tw-bg-background hover:tw-bg-muted/50 tw-transition-colors"
                        onClick={() => setShowExtraPlans(!showExtraPlans)}
                    >
                        <span className="tw-font-medium tw-text-foreground">{i18next.t('plans.view_extra_plans')}</span>
                        {showExtraPlans ? (
                            <ChevronDown className="tw-w-5 tw-h-5 tw-text-muted-foreground"/>
                        ) : (
                            <ChevronRight className="tw-w-5 tw-h-5 tw-text-muted-foreground"/>
                        )}
                    </button>

                    {showExtraPlans && (
                        <div className="tw-border-t tw-border-solid tw-border-border tw-p-4">
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                                <Card className="card plan tw-h-full tw-p-4 tw-flex tw-flex-col">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-mb-4">
                                        <div className="tw-text-center tw-font-bold tw-text-lg tw-mb-0.5 tw-text-primary">{essentialsPlan.key}</div>
                                        <h4 className="tw-font-bold tw-text-2xl !tw-mb-0">€{essentialsPlan.price}/mo</h4>
                                        <span className="tw-text-xs tw-text-muted-foreground">{i18next.t('plans.starting_at')}</span>
                                    </div>
                                    <CardContent className="tw-flex-1 tw-flex tw-flex-col">
                                        <div className="tw-flex tw-flex-col">
                                            <div>
                                                <p className="tw-font-bold tw-mb-2">{i18next.t('plans.features_label')}:</p>
                                                <FeatureList className="tw-text-primary" features={addSpacersToFeatures(essentialsPlan.features, plans)}/>
                                            </div>
                                            <div className="tw-border-t tw-border-solid tw-border-primary tw-my-3"></div>
                                            <div>
                                                <ConditionsList className="tw-text-primary" conditions={essentialsPlan.conditions}/>
                                            </div>
                                        </div>
                                        <ExtraSection className="tw-text-primary" extra={essentialsPlan.extra}/>
                                        {
                                            WiseMetering.Permissions.hasRolePermissions('plans', 'update') && (
                                                <CardFooter>
                                                    {allowedTransitions['essentials'] && (
                                                        <Button className="tw-w-full" onClick={() => handleChangePlan('essentials')} variant="secondary">
                                                            {`${i18next.t('plans.switch_to')} ${essentialsPlan.key}`}
                                                        </Button>
                                                    )}
                                                </CardFooter>
                                            )
                                        }
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {!WiseMetering.Permissions.hasRolePermissions('plans', 'update') && (
                <div className="tw-mt-4 tw-text-right">
                    <StandaloneLink href="mailto:support@wisemetering.com" text={i18next.t('plans.custom_requirements_contact')}/>
                </div>
            )}
        </>
    );
}
